import React, { useRef, useState, useCallback, useEffect } from "react";

/**
 * 관리자가 작성한 HTML(공지 등)을 iframe(srcdoc + sandbox)으로 격리 렌더한다.
 *
 * 배경:
 *  - 기존에는 공지 내용을 dangerouslySetInnerHTML로 콘솔 DOM에 직접 주입했다.
 *  - 그 내용에 <style>(특히 `table {}`, `td {}`, `body {}` 같은 전역 선택자)이 있으면
 *    콘솔 페이지 전체(예: 실시간 참가자 테이블 .question_storage_table)로 스타일이 번졌다.
 *  - 또한 임의 HTML 주입이라 <script> XSS 위험도 있었다.
 *
 * 해결:
 *  - iframe은 별도 document라 내부 <style>이 부모 페이지로 새지 않는다(CSS 격리).
 *  - sandbox="allow-same-origin"만 부여 → 스크립트 실행 차단(XSS 완화),
 *    동시에 부모가 내부 높이를 측정해 자동 조절할 수 있게 한다(allow-scripts는 주지 않음).
 *
 * 높이는 내용에 맞춰 onLoad + ResizeObserver로 자동 조절한다.
 */
function IsolatedHtml({ html }) {
  const ref = useRef(null);
  const roRef = useRef(null);
  const [height, setHeight] = useState(0);

  const resize = useCallback(() => {
    const doc = ref.current?.contentDocument;
    if (!doc?.body) return;
    const h = Math.ceil(
      doc.documentElement?.scrollHeight || doc.body.scrollHeight || 0
    );
    if (h) setHeight((prev) => (prev === h ? prev : h));
  }, []);

  const onLoad = useCallback(() => {
    resize();
    const doc = ref.current?.contentDocument;
    if (doc?.body && typeof ResizeObserver !== "undefined") {
      try {
        roRef.current?.disconnect();
        const ro = new ResizeObserver(() => resize());
        ro.observe(doc.body);
        roRef.current = ro;
      } catch (e) {
        // ResizeObserver 미지원/접근 실패 시 onLoad 측정값만 사용
      }
    }
  }, [resize]);

  useEffect(
    () => () => {
      try {
        roRef.current?.disconnect();
      } catch (e) {}
    },
    []
  );

  // 기존 dangerouslySetInnerHTML은 <body> 태그가 제거돼 작성자의 body{} 규칙이
  // 공지 본문에 적용되지 않았다(= 페이지로 새던 부작용). iframe은 실제 <body>가 있어
  // body{padding/margin}이 본문에 적용돼 폭/여백이 달라진다.
  // 옛 렌더와 동일한 레이아웃을 유지하기 위해 body/html의 margin·padding을 중화한다.
  // (내부 .wrap 등 작성자의 콘텐츠 스타일은 그대로 유지)
  const NORMALIZE_STYLE =
    "<style>html,body{margin:0 !important;padding:0 !important;}</style>";

  return (
    <iframe
      ref={ref}
      title="notice-content"
      srcDoc={(html || "") + NORMALIZE_STYLE}
      sandbox="allow-same-origin"
      onLoad={onLoad}
      scrolling="no"
      style={{
        width: "100%",
        border: 0,
        display: "block",
        height: height ? `${height}px` : "0px",
      }}
    />
  );
}

export default IsolatedHtml;
